import {getIntlTranslation, INTL_ACTION, INTL_ENTITY, F} from '@/utils/intl';
import {Button, message} from 'antd';
import React from 'react';
import {useDispatch, useIntl, useSelector} from 'umi';
import {deleteCalendarEventRequest} from '@/services/api/serviceRequest';
import * as ServiceRequestSelectors from '@/selectors/serviceRequestSelectors';
import {DeleteOutlined} from '@ant-design/icons';
import {isRecurringEvent} from '@/utils/recurrence';
import {useRecurrenceScope} from '@/components/Kanban/ServiceRequests/Calendar/Recurrence/useRecurrenceScope';
import {RecurrenceScope} from '@/typings/models/Recurrence';
import {getAPIErrorMessage, processAPIResponseNow} from '@/utils/request';
import {dispatchCalendarEventRemoval} from '@/components/Kanban/ServiceRequests/Calendar/utils';
import {confirmCalendarEventDelete} from '@/components/Kanban/ServiceRequests/Calendar/EmailDecision';


const RemoveCalendarEventAction = ({
                                     children = null,
                                     calendarEvent,
                                     setLoading = (loading: boolean) => {
                                     },
                                     onSuccess = () => {
                                     },
                                     // Legacy Popconfirm-control props, swallowed so they don't
                                     // leak onto the Button element (the confirm is a Modal now).
                                     open,
                                     onOpenChange = () => {},
                                     buttonSize = 'small',
                                     showActionText = false,
                                     ...buttonProps
                                   }) => {
  // Calendar events are not gated by control_emails — anyone can pick. Only
  // lead-linked events notify a customer, so the choice is offered just for those.
  // Defaults to off.
  const isLeadLinked = !!(calendarEvent?.lead || calendarEvent?.lead_id || calendarEvent?.leadId);
  const showEmailChoice = isLeadLinked;
  const intl = useIntl();
  const { askRecurrenceScope, recurrenceScopeDrawer } = useRecurrenceScope();
  const recurring = isRecurringEvent(calendarEvent);
  const dispatch = useDispatch();
  const detailsDrawer = useSelector(ServiceRequestSelectors.selectCalendarEventDetailsDrawer);

  const performRemove = (deleteScope?: RecurrenceScope, sendEmail = false) => {
    if (setLoading)
      setLoading(true)
    return deleteCalendarEventRequest({
      params: {
        calendarEventId: calendarEvent.id
      },
      // Nothing to notify without a linked customer: send no `send_email` at
      // all so the backend keeps its own notification default.
      ...(showEmailChoice ? { data: { send_email: sendEmail } } : {}),
      // Series delete carries the chosen scope as a query param; a single event stays queryless.
      ...(deleteScope ? { query: { apply_to: deleteScope } } : {}),
    }).then((result) =>
      processAPIResponseNow(result, {
        onEnd: () => setLoading && setLoading(false),
        onSuccess: () => {
          // Reflect the removal in the store right away — the websocket echo
          // (calendar_event_deleted / recurring_*) does the same for other
          // clients and is idempotent with this.
          dispatchCalendarEventRemoval(dispatch, calendarEvent, deleteScope);
          // Close the details drawer right away when it's showing this event,
          // instead of waiting for the websocket round-trip to toggle it.
          if (detailsDrawer?.open && detailsDrawer?.calendarEventId === calendarEvent.id) {
            dispatch({ type: 'serviceRequest/toggleCalendarEventDetailsDrawer' });
          }
          return (onSuccess as (r?: any) => any)(result);
        },
        onError: (data) => {
          message.error(getAPIErrorMessage(data));
          // A failed delete usually means the calendar is stale (e.g. the
          // occurrence was already removed via a series-wide scope) — refetch
          // so ghost events disappear instead of dead-ending again.
          dispatch({ type: 'serviceRequest/updateFetchCalendarViewAt' });
        },
      }),
    )
  }

  const handleRemove = async () => {
    let scope: RecurrenceScope | undefined;
    if (recurring) {
      scope = await askRecurrenceScope({ action: 'delete' }) ?? undefined;
      if (!scope) return;
      // The scope drawer is already the confirmation step; only ask again when
      // there is a customer to notify.
      if (!showEmailChoice) return performRemove(scope, false);
    }

    // Delete confirmation popup with the notify-customer choice — same pattern
    // as the drag-and-drop reschedule confirm.
    const { confirmed, sendEmail } = await confirmCalendarEventDelete({
      title: getIntlTranslation(intl, {
        entity: INTL_ENTITY.APPOINTMENT,
        action: INTL_ACTION.REMOVE_CONFIRMATION,
      }),
      emailChoiceLabel: intl.formatMessage({
        id: 'pages.calendar.sendEmail.cancel',
        defaultMessage: 'Notify the customer by email',
      }),
      showEmailChoice,
    });
    if (!confirmed) return;
    return performRemove(scope, sendEmail);
  };

  const defaultButton = (
    <Button size={buttonSize}
            shape={"round"} color={"danger"} variant={"filled"}
            data-testid="calendar-event-delete-btn"
            icon={<DeleteOutlined/>} {...buttonProps}>
      {showActionText && <F id={'common.actions.remove'} defaultMessage={'Delete'}/>}
    </Button>
  );

  return <>
    {children
      ? React.cloneElement(children as React.ReactElement, { onClick: handleRemove })
      : React.cloneElement(defaultButton, { onClick: handleRemove })}
    {recurrenceScopeDrawer}
  </>;
}

export default RemoveCalendarEventAction
